/**
 * `Settings -> Business figures` (VRG-459).
 *
 * The figures a brand supplies that analytics metrics are calculated from — annual
 * return-fraud loss, gross margin, customer acquisition cost. Onboarding surface and
 * audit surface both: every field states what it drives and where its value came
 * from, because the failure mode this feature exists around is a brand disputing its
 * own dashboard, and "who entered this" is the first question in that conversation.
 *
 * ## ⚠️ THE FIELD LIST IS THE SERVER'S CATALOGUE, NEVER A LIST WRITTEN HERE
 *
 * `GET /analytics/admin/inputs` derives the catalogue from `METRIC_REGISTRY`: a
 * metric declares `inputs: MetricInputKey[]`, and the fields worth collecting are
 * the union of those across metrics that are not blocked. Three surfaces render
 * from it — this page, the in-card popover, and later the marketing site's signup
 * form — so a hand-written list on any one of them stops asking for what a metric
 * needs the day someone adds an input to it, and the metric then renders an
 * em-dash with no route to fixing it. Nothing on this page names a field.
 *
 * ## Why it is not a tab on `Settings -> Fields`
 *
 * Fields was considered and rejected on a naming test that turned out to be a
 * taxonomy test. Its tabs complete its name — "Field Mapping" (where a field's data
 * comes from), "Field Defaults" (what a field falls back to) — and there is no
 * honest completion for "your company's annual return-fraud loss". Mapping and
 * Defaults are properties OF A FIELD, schema-level. These are company-level
 * business facts. A different object. ("Field Values" is the near-miss that sounds
 * right for two seconds: a metric input is not a value of a field, it is a figure
 * about the business.) Worth revisiting only if Fields is renamed to something
 * broader — the objection is to the taxonomy the current name asserts.
 *
 * ## Permission (§27)
 *
 * Reading is `view_analytics` (server-side too). Editing is `analytics_input_edit`.
 * Without it the page still renders, values and provenance included, with every
 * control disabled and a tooltip naming the permission. Hiding the page would make
 * the figures it drives unauditable to the very people allowed to see them.
 */
import { useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Box, Grid, Link, Stack, Theme, Typography, useTheme } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { ArrowUpRightIcon, ShieldIcon } from 'lucide-react'

import { Button, Card, FIELD_HEIGHT, Skeleton } from '@repo/ui'
import {
  METRIC_ROLE_LABELS,
  METRIC_SOLUTION_LABELS,
  MetricRole,
  MetricSolution,
  DEFAULT_REPORTING_CURRENCY,
} from '@repo/types'
import type { IRoiSummary, MetricInputKey } from '@repo/types'

import { DEFAULT_COMPANY_ID } from 'config'
import { useSnackbar } from 'contexts'
import { useReferrerState } from 'hooks'
import {
  useCompany,
  useMetricInputs,
  useRemoveMetricInput,
  useSetMetricInputs,
} from 'hooks/api'
import { createApiCall } from 'services'
import { ROUTES } from 'routes'
import { StickyFormHeader } from 'components/navigation/StickyFormHeader/StickyFormHeader'
import { useSettingsStickyOffset } from 'pages/settings/SettingsPage/settingsStickyOffset'
import { getErrorMessage } from 'utils'
import {
  MARKER_GLYPHS,
  MARKER_LABELS,
  MetricInputField,
  metricInputMarkerState,
  metricInputTaxonomy,
  PROSE_MEASURE_PX,
  valueForKey,
  type MetricInputMarkerState,
} from 'components/analytics/metricInputs'
import { Search } from 'components/input/Search'
import { SELECT_FIELD_FOOTER_ROW_SX, SelectField } from 'components/input/fields/SelectField'
import { METRIC_BY_ID } from '@repo/types'

/**
 * The ROI response the consequence echo is stated against.
 *
 * Fetched with react-query and NOT through `useAnalyticsRoi`, deliberately. That
 * hook writes into `state.analytics.roi` — a single redux slot the reducer
 * overwrites wholesale — which this epic has already paid for twice as a
 * last-writer-wins race. A settings page has no business mutating the analytics
 * page's data; it only needs to read a summary.
 *
 * ## The window is the company's whole lifetime, matching the hero's default
 *
 * A multiple only means something against a stated period. The ROI hero defaults to
 * "All time", whose lower bound is the company's own `created_at`, so quoting the
 * same window here means the "moves from 2.9x to 4.1x" a reader sees on this page is
 * the number they will see on the hero — rather than two different true figures that
 * read as a contradiction.
 */
const useLifetimeRoi = (createdAt?: string | null) => {
  const query = useQuery({
    queryKey: ['analytics', 'roi', 'lifetime', createdAt ?? null],
    queryFn: async (): Promise<IRoiSummary | null> => {
      const response = await createApiCall({
        method: 'GET',
        url: '/analytics/admin/roi',
        params: {
          from: createdAt,
          to: new Date().toISOString(),
          // The API returns a `roi` summary ONLY when the request names a single
          // company, and omits it otherwise rather than summing bills across brands.
          company_ids: [DEFAULT_COMPANY_ID],
        },
      })
      return (response.data?.roi as IRoiSummary | undefined) ?? null
    },
    enabled: Boolean(createdAt),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  })

  return { roi: query.data ?? null, refetch: query.refetch }
}

/**
 * ── THE TOOLBAR'S WIDTHS, AND THE ARITHMETIC BEHIND THEM ─────────────────────────────
 *
 * ⚠️ Every number here is MEASURED in the browser, not estimated. Re-measure rather than
 * adjusting by eye — the whole reason the previous layout shipped a truncated trigger is
 * that a plausible-looking number was 31px short.
 *
 * The settings column is not one width. Measured at 1280x800:
 *
 *     1100px   wide screen (NARROW_COLUMN_WIDTH)
 *      935px   sidebar COLLAPSED
 *    **788.5px sidebar EXPANDED** — the tightest case, and the one that governs
 *
 * ## What changed, and why the old floors are gone
 *
 * Search, sort and the three filters used to share ONE row. At 788.5 that left 740.5px of
 * control across five slots, and the row laid out at EXACTLY its floors — measured
 * 140.5 + 90 + 142 + 218 + 150 + 48 of gaps = 788.5, i.e. zero slack anywhere. Two labels
 * could not fit at those floors and the file said so rather than papering over it:
 *
 *     "Sustainability & Compliance"       195.2 + 54 = 249px needed vs 218 available
 *     "Traceability & Logistics" + "+2"   181.3 + 54 = 235px needed vs 218 available
 *
 * The progress line has since moved down into the search's row and the filters have taken
 * a row of their own, so the contest is over: FOUR controls now share 788.5px with three
 * 12px gaps = 752.5px of control, against five slots' 740.5px before. The floors below are
 * therefore not floors at all any more — they are fixed widths sized so that nothing on
 * this row can truncate in ANY selection state.
 *
 * ## The sizing rule: text budget = W − 56
 *
 * A `SelectField` trigger spends **54px** on chrome — measured, `padding-left: 14px` plus
 * `padding-right: 40px` for the chevron — and `MultiSelectSummary`'s `fit()` requires
 * `width <= available - FIT_MARGIN_PX` with `FIT_MARGIN_PX = 2`. So a trigger of width W
 * shows at most **W − 56** pixels of text.
 *
 * The taxonomy labels, measured at the trigger's own 16px Lato:
 *
 *     Legal          36.7   Needed        55.1   Brand Protection            120.3
 *     Team           37.4   Logistics     60.5   Resale & Circularity        138.4
 *     Admin          46.0   Analytics     64.7   Traceability & Logistics    159.7
 *     Design         48.5   Marketing     72.7   Consumer Engagement         163.1
 *     Finance        54.9   Purchasing    77.7   Sustainability & Compliance 195.2
 *     Supplied       60.6   Engineering   83.7   " +N" count                  21.6
 *     Needs review   95.4
 *
 * ⚠️ THE WORST CASE IS "LONGEST LABEL + A COUNT", NOT THE LONGEST LABEL. `MultiSelectSummary`
 * shows as many labels as fit and then a `+N`, so a selection whose FIRST label is the long
 * one renders `<longest> +N`. Sizing for the bare label leaves that case truncated, which is
 * exactly half of the defect this replaced. Each width is therefore
 * (longest label + 21.6 + 56), rounded up:
 *
 *     sort       90 = 32.6  ("A - Z", single-select, no count) + 56,  +1.4 slack
 *     role      170 = 83.7  ("Engineering")           + 21.6 + 56,  +8.7 slack
 *     solution  280 = 195.2 ("Sustainability & Comp") + 21.6 + 56,  +7.2 slack
 *     status    180 = 95.4  ("Needs review")          + 21.6 + 56,  +7.0 slack
 *     --------------------------------------------------------------------------
 *     720 + 36 of gaps = **756px against the 788.5 available**, 32.5px spare
 *
 * The widths are deliberately NOT equal. The axes do not carry equally long words, and
 * giving Status the width Solution needs would spend 100px on a three-option enum. Each
 * control being exactly as wide as its own longest content is what makes the row scannable.
 *
 * ## ⚠️ RESOLVED BY DELETING CONTROLS, NOT BY SHRINKING THEM
 *
 * ⚠️ THE GOVERNING WIDTH IS 762, NOT THE 788.5 THIS BLOCK USED TO SAY. Re-measured in the
 * browser at 1280x800, sidebar expanded: the row's container runs x=493 to x=1255. Trust
 * this number over the 788.5 still quoted in the column table above, which no longer
 * reproduces — and re-measure rather than adjusting by eye, per this block's own opening.
 *
 * The two-row shape this block used to describe is gone. It was: a search row here, and a
 * `Filter:` [Role][Solution][Status] … `Sort:` [A - Z] row in flow below, which needed
 * **846.4px** and therefore WRAPPED at the expanded-sidebar width — the one this page is
 * most often read at.
 *
 * The block's own conclusion was that the deficit could not be reclaimed from the controls:
 * exact text budgets save 22px, tighter label gaps another 8, and every control then sits at
 * zero slack and truncates the day a label is renamed. That conclusion was right. The
 * deficit was reclaimed from the CONTROL COUNT instead.
 *
 *     Sort   [A - Z]      131.6px   its own label had to explain it was a sort
 *     Status [Any status] 192.0px   the header progress line writes the same `statusFilter`
 *     `Filter:` / `Sort:`  78.4px   the only rendered instances of that pattern in the app
 *
 * What is left is ONE row, and at 762 it lands exactly:
 *
 *     search 238 + 12 + role 220 + 12 + solution 280  =  762   MEASURED, no wrap
 *
 * The search is `flex: 0 1 300px`, so it asks for 300, absorbs the whole 62px deficit and
 * settles at 238. (The `<input>` inside it measures 186 — the field spends 52px on the
 * magnifier, the clear "×" and its padding. Measure the FIELD, not the input, or you will
 * conclude the search is 50px narrower than it is.)
 *
 * Note what this buys against the alternative: keeping Status needs 1016 and would leave the
 * search 12px, which is not a search box.
 *
 * ⚠️ THE ANTI-TRUNCATION WIDTHS BELOW STILL GOVERN. Role's 220 and Solution's 280 are sized
 * so no selection state can truncate, and the 35.5px overrun is paid by the search rather
 * than by them, deliberately — the search has a `flex: 0 1` that is designed to shrink, and
 * they do not. Do not reclaim width from a filter.
 *
 * ⚠️ SOLUTION'S WIDTH FOLLOWS `MetricSolution`. If a solution is renamed or added,
 * re-measure. Six solutions is confirmed correct — Sustainability & Compliance stays,
 * because compliance metrics are coming — so 195.2 is a permanent floor, not a transitional
 * one. (Status's width followed `MARKER_LABELS` and went with the control; it was once sized
 * for "Unconfirmed" (91.9) and was 3.4px too small the moment that pill became "Needs
 * review" (95.4), which is the measurement worth remembering if that dropdown ever returns.)
 *
 * ## The search box is CAPPED, not greedy
 *
 * Left to `flex: 1 1 auto` it took ALL the slack that freed up when the progress line moved:
 * measured at **568.5px**, up from the 140.5px it was squeezed to when five controls shared
 * one row. Wider was right — 140.5 was indefensible — but 568.5 overshot: a single-line
 * finder over a four-card list does not need half the column, and at that width the field
 * reads as a text area rather than as a search box.
 *
 * ⚠️ 300 IS MATCHED FROM THE PLATFORM, NOT PICKED. Two things independently say 300:
 *
 *   - `Settings -> Integrations` — the closest peer there is, a settings page with a
 *     standalone (non-table-toolbar) search over a card list — sets `maxWidth: '300px'`.
 *     `DocSectionNav` sets the same.
 *   - the shared `Search` component itself hardcodes `minWidth: 300` internally, so 300 is
 *     the width it is BUILT to rest at. Anything narrower is fighting the component (see the
 *     `minWidth: 0` note at the call site, which exists to win that fight in a narrow column).
 *
 * So the cap is the platform's own number rather than a taste call, and it happens to be the
 * one width at which the override and the component agree instead of contradicting.
 *
 * The box still SHRINKS below 300 in a column too narrow for it (`flex: 0 1`), which is what
 * `minWidth: 0` is retained for. It just never grows past it.
 */
const SEARCH_WIDTH_PX = 300
const SEARCH_PLACEHOLDER = 'Search figures'

/**
 * Fixed widths, one per axis. See the block above for the arithmetic; the short version is
 * (longest label + a "+N" count + 56px of chrome), so no selection state can truncate.
 *
 * ⚠️ `SORT_WIDTH_PX` (90) and `STATUS_WIDTH_PX` (180) were here and are gone with their
 * controls. Do not reinstate a width without reinstating the control it sized — an unused
 * width constant reads as a slot the row is still reserving.
 */
/**
 * ⚠️ 220, NOT the 170 the text budget alone would give it.
 *
 * 170 was the arithmetic answer — "Engineering" (83.7) + a "+N" count (21.6) + 56 of chrome =
 * 161.3 — and it was correct about truncation and wrong about the row. It made Role visibly
 * the runt beside Solution's 280 and Status's 180, and a control sized to its longest single
 * label is a control that shows exactly ONE role before collapsing to a count: pick Finance
 * and Legal and you get "Finance +1" rather than both, in a box with 8.7px to spare.
 *
 * 220 buys 50px of text budget (164 against 114), which is enough for a second short label —
 * "Finance, Legal" is 91.6 — so the common two-role selection reads as two roles. That is the
 * point of a multiselect trigger, and the reason a width can legitimately exceed its
 * anti-truncation floor.
 */
const ROLE_WIDTH_PX = 220
const SOLUTION_WIDTH_PX = 280

/**
 * The progress line's reserved width, so the search box's right edge does not jump as the
 * sentence changes length.
 *
 * 207.5 is "12 of 12 supplied · 12 need review" measured at the line's own 14px Lato — a
 * catalogue three times today's length. Without a reserved width the search box would
 * resize on every save, which is motion attached to a number the reader is trying to read.
 */
const PROGRESS_WIDTH_PX = 208

/**
 * The loading card's height, matching a loaded one (§19 footprint parity).
 *
 * 328, MEASURED on all four live cards at 1280x800 with the sidebar expanded. They are all
 * the same height because they are `height: 100%` inside a `Grid` row and the tallest governs
 * — see the part-by-part breakdown at the skeleton card itself.
 *
 * ⚠️ It is a fixed number because a skeleton has no content to size itself from, and it is the
 * one number here that can go stale: it follows the card's parts, so re-measure it whenever a
 * part is added to or removed from `MetricInputField` (it was 371 for the old bordered
 * "Used by" panel, and stayed 371 for a while after that panel became a one-line label).
 */
const SKELETON_CARD_HEIGHT_PX = 328

/**
 * The two counts in the progress line are BUTTONS that apply the Status filter.
 *
 * ## A real `<button>` that renders as plain text
 *
 * They sit inside a sentence, so a `Button` is out — a 40px control cannot live mid-line,
 * and §60 forbids shrinking one to fit. A bare `onClick` on a `<span>` is out too: it is
 * unreachable by keyboard, which is the half that matters most for something with no visual
 * affordance at rest.
 *
 * So: a real `<button>` stripped back to inherit the sentence's own font and colour, with no
 * decoration until it is active. The keyboard affordance is the focus ring, which is NOT
 * what "make them look like plain text" gave up — a control reachable by Tab with no visible
 * focus state is a different and worse problem.
 *
 * ## The pressed state is the half that keeps the page honest
 *
 * `aria-pressed` is not just for assistive tech here: 600 weight is how a reader sees,
 * without looking down at the dropdown, that the list below is currently narrowed BY this
 * count. Clicking a lit count clears it. Without that, the header could apply a filter it
 * gave no sign of having applied.
 *
 * `font: 'inherit'` and `color: 'inherit'` are load-bearing — a UA stylesheet gives buttons
 * their own family, size and colour, so without these the counts would render as system-font
 * text inside a Lato sentence.
 */
const COUNT_FILTER_SX = {
  font: 'inherit',
  color: 'inherit',
  background: 'none',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  /*
    ⚠️ NO UNDERLINE, at rest or on hover. These read as plain text until they are ACTIVE,
    and then they go bold.

    The first version carried a dotted underline at rest going solid on hover, on the
    argument that a control has to look like one. It was rejected, and the objection is
    sound: this is a COUNT first and a control second, and a permanent rule under both
    halves of the sentence turned the page's one piece of status prose into something that
    read like two links. Bold-when-active is the stronger signal anyway — it says which
    filter is applied, which is the thing worth showing, rather than advertising that two
    words are clickable.

    Hover is carried by `opacity` rather than by weight or decoration: a weight change on
    hover would reflow the text under the cursor, and this sits in a fixed-width
    right-aligned slot where that reads as the sentence twitching.
  */
  '&:hover': { opacity: 0.7 },
  '&[aria-pressed="true"]': { fontWeight: 600 },
  /*
    A real, visible ring — NOT §31's `brand['600-6%']` soft ring. That token is 6% alpha and
    is only legible because a field ALSO changes its border colour alongside it; on bare
    inline text with no border there is nothing for it to reinforce, and it would amount to
    removing the UA focus ring and replacing it with nothing.
  */
  '&:focus-visible': {
    outline: (theme: Theme) => `2px solid ${theme.palette.brand[500]}`,
    outlineOffset: '2px',
    borderRadius: '2px',
  },
}

export const BusinessFiguresPage = () => {
  const { open: notification } = useSnackbar()
  // For the status options' glyph tints — the same two tones the card pills use.
  const theme = useTheme()

  /**
   * Where this page's own sticky header pins.
   *
   * ⚠️ NOT 0. The settings shell's "Settings" header is ITSELF sticky, so a section
   * header at 0 parks underneath it — invisible, and still covering the content it
   * was meant to sit above. Nor is it a constant: the shell header grows by a 47px
   * back-link row when a cross-surface referrer brought the user in (§42), so a
   * hardcoded number is wrong in exactly one of the two arrival paths.
   * `SettingsPage` already computes the height for its own sub-nav; this reads the
   * same number rather than re-deriving it.
   */
  const stickyTop = useSettingsStickyOffset()

  // §42 — the symmetric half: jumping out to Analytics carries this page as the origin,
  // so the analytics page offers a way back.
  const referrer = useReferrerState('Business figures')

  const companyQuery = useCompany({ id: DEFAULT_COMPANY_ID })
  const { roi, refetch: refetchRoi } = useLifetimeRoi(
    (companyQuery.data as { created_at?: string } | undefined)?.created_at,
  )

  const { catalogue, values, data, isLoading } = useMetricInputs()
  const setInputs = useSetMetricInputs()
  const removeInput = useRemoveMetricInput()

  const saving = setInputs.isPending || removeInput.isPending

  const handleSave = async (key: MetricInputKey, storedValue: number, currency: string) => {
    try {
      const response = await setInputs.mutateAsync([
        {
          key,
          value: storedValue,
          /*
            The unit the FIELD was denominated in, forwarded from `MetricInputField` —
            not a hardcoded 'USD' and not the company's current reporting currency. A
            figure is stored in the currency it was ENTERED in; see VRG-497.

            Still sent unconditionally rather than branching: an amount with no currency
            is a 400, not a default.
          */
          currency,
        },
      ])
      /*
        ⚠️ WHAT THIS MESSAGE MAY CLAIM. `unblocked_metric_ids` means "has every input
        it declares", which is NOT "renders a figure": a metric can appear there and
        still be withheld as `not_computed`, because Vera cannot produce the measured
        half of its formula. So the copy reports what was SAVED and how many metrics
        now have what they asked for, and the refetched analytics page is what says
        whether a number appeared.
      */
      const count = response.unblocked_metric_ids.length
      notification({
        text:
          count > 0
            ? `Saved. ${count} ${count === 1 ? 'metric has' : 'metrics have'} every figure ${count === 1 ? 'it' : 'they'} asked for.`
            : 'Saved.',
        type: 'success',
      })
      refetchRoi()
    } catch (error) {
      notification({ text: getErrorMessage(error), type: 'error' })
    }
  }

  const handleRemove = async (key: MetricInputKey) => {
    try {
      await removeInput.mutateAsync(key)
      // "Cleared", matching the button — the toast is the confirmation of the action the
      // reader just took, so it has to use that action's word.
      notification({
        text: 'Cleared. The metrics it fed will ask for it again.',
        type: 'success',
      })
      refetchRoi()
    } catch (error) {
      notification({ text: getErrorMessage(error), type: 'error' })
    }
  }

  /**
   * The page's progress line, and the per-card markers, from ONE derivation.
   *
   * Counted from the catalogue rather than from `values.length`: a stored value whose
   * key has left the catalogue is filtered out server-side, so the two can differ and
   * only the catalogue-relative count is meaningful to a reader looking at the form.
   *
   * ⚠️ `unconfirmedCount` USED TO BREAK THAT RULE — it counted `values.filter(provisional)`
   * across every stored value while `answered` counted across the catalogue, so an
   * orphaned provisional key could be counted here with no card on the page to confirm
   * it. Both now walk the catalogue through `metricInputMarkerState`, which is the same
   * function each `MetricInputField` renders its marker from. So the invariant holds by
   * construction rather than by comment:
   *
   *     cards showing a marker            = "N supplied"
   *     cards showing the AMBER marker    = "· K unconfirmed"
   *
   * A provisional figure is in both terms, exactly as the sentence reads: "unconfirmed"
   * qualifies the supplied count, it does not name a separate bucket.
   */
  const markerStates = useMemo(
    () => catalogue.map(d => metricInputMarkerState(valueForKey(values, d.key))),
    [catalogue, values],
  )
  const answered = useMemo(
    () => markerStates.filter(s => s !== 'unset').length,
    [markerStates],
  )
  const unconfirmedCount = useMemo(
    () => markerStates.filter(s => s === 'unconfirmed').length,
    [markerStates],
  )

  /**
   * The finder. Client-side, because the whole catalogue is already in hand.
   *
   * `debounce={false}`: debouncing exists to spare a SERVER a request per keystroke.
   * There is no request here — the list is filtered in memory — so a delay would be
   * latency added for nothing on a list short enough to filter instantly.
   */
  const [query, setQuery] = useState('')

  /**
   * ── SORT AND THE TWO TAXONOMY FILTERS ────────────────────────────────────────────
   *
   * Two multiselects rather than a filter drawer, deliberately: there are exactly two
   * axes and both are closed enum sets, so a drawer would be a modal wrapper around
   * two dropdowns.
   *
   * Sort and the filters share a row of their OWN, below the pinned search row and
   * outside the sticky block — so none of this costs pinned height either, but for a
   * different reason than before: it is not pinned at all. See the pin block in the
   * render for the measurement that put it there.
   */
  const [roleFilter, setRoleFilter] = useState<string[]>([])
  const [solutionFilter, setSolutionFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])

  /*
    Options come from the ENUM plus its label map, never a hand-typed list — the same
    rule the field catalogue itself follows. A solution added to `MetricSolution` shows
    up here with no edit, which is the only way this filter can stay truthful.

    ⚠️ THAT YIELDS SIX SOLUTIONS, and the request said "the 5 from the website".
    `MetricSolution` carries Resale & Circularity, Brand Protection, Traceability &
    Logistics, AI & Analytics, Consumer Engagement AND Sustainability & Compliance. The
    enum's own docstring says these values ARE the marketing site's top-level groupings
    and that "changing one changes a published page" — so either the site has five and
    the sixth was added here without it, or the site has six. Rendering five would mean
    hardcoding a subset and silently hiding whichever metrics are tagged with the one
    left out, so this renders what the code declares and the discrepancy is reported
    rather than resolved by guesswork.
  */
  const roleOptions = useMemo(
    () =>
      Object.values(MetricRole).map(r => ({ label: METRIC_ROLE_LABELS[r], value: r as string })),
    [],
  )

  /**
   * "View roles" — pinned below the Role options, in the dropdown's sticky footer slot.
   *
   * These role names are the company's own roles (`MetricRole` is deliberately a subset of
   * `RoleName`), so "which metrics does Finance care about" is one question away from
   * "what IS Finance allowed to do" — and the answer to the second lives on a page the
   * reader has no reason to know exists from here.
   *
   * It carries `referrer` (§42), so the roles page renders "← Back to Business figures"
   * and returning restores this page rather than dumping the reader in Settings. That is
   * the case §42 exists for: a lateral jump between records, not a drill-in a breadcrumb
   * could describe.
   *
   * Styling is `SELECT_FIELD_FOOTER_ROW_SX` from `SelectField` itself, not a third private
   * copy of the same object (§36) — `InviteCompanyUserModal` renders the identical row,
   * with the identical icon, for the identical destination.
   */
  const roleFooter = (
    <Box
      component={RouterLink}
      to={ROUTES.SETTINGS_COMPANY_ROLES}
      state={referrer}
      sx={{ ...SELECT_FIELD_FOOTER_ROW_SX, textDecoration: 'none' }}
    >
      <ShieldIcon size={16} strokeWidth={1.5} />
      <Typography variant="body3">View roles</Typography>
    </Box>
  )
  const solutionOptions = useMemo(
    () =>
      Object.values(MetricSolution).map(s => ({
        label: METRIC_SOLUTION_LABELS[s],
        value: s as string,
      })),
    [],
  )

  /**
   * ── THE STATUS AXIS, AND HOW IT IS KEPT HONEST AGAINST THE HEADER ────────────────
   *
   * The header reads "N of M supplied · K unconfirmed". If the filter offered a "Set"
   * that excluded provisional figures, its result count would disagree with N on the
   * very page it sits on — two readings of the same data, contradicting each other.
   *
   * So the options are the header's OWN nouns, and they nest exactly as the sentence
   * does: "· K unconfirmed" QUALIFIES the supplied count, it does not partition it.
   *
   *     Supplied      confirmed + unconfirmed   =  N
   *     Unconfirmed   provisional only          =  K   (a SUBSET of Supplied)
   *     Needed        unset                     =  M − N
   *
   * Picking Supplied and Unconfirmed together yields Supplied, which is correct rather
   * than a quirk — a subset OR'd with its superset is the superset.
   *
   * `metricInputMarkerState` is the one classifier: the header counts, these options and
   * the per-card marker all read it, so none of the three can drift.
   */
  const STATUS_MATCHERS: Record<string, (s: MetricInputMarkerState) => boolean> = {
    supplied: s => s !== 'unset',
    unconfirmed: s => s === 'unconfirmed',
    needed: s => s === 'unset',
  }
  /*
    ⚠️ THE LABELS COME FROM `MARKER_LABELS`, the same map the per-card pills render from.

    They used to be typed here — "Supplied" / "Unconfirmed" / "Needed" — which was fine
    only for as long as nobody renamed a pill. The moment "Unconfirmed" became "Needs
    review" on the card, a hand-typed option here would have offered a filter whose name
    matched nothing visible on the page it filters. Reading the map makes that class of
    drift impossible rather than merely unlikely.

    "Supplied" is `confirmed`'s label doing double duty, and that is correct rather than a
    coincidence: the header sentence counts confirmed AND unconfirmed figures as supplied,
    so the option that means "has a value" is named with the same word the count uses.
  */
  /*
    ── ⚠️ THE OPTION IS A GLYPH PLUS A LABEL, NOT THE PILL ─────────────────────────────

    Each row carries its state's own glyph from `MARKER_GLYPHS` — the same map the card pills
    render from, so the mark in the menu is the mark on the card by construction.

    It is deliberately NOT the pill itself. A filled pill inside a menu row puts a second
    background inside a rectangle that already has its own hover and selected treatment, and
    two backgrounds fighting in one row is the objection that settled tint-versus-check for
    selected rows. The glyph carries the tie to the card at none of that weight, and §21
    wants a leading icon on every menu row regardless.

    The glyph is tinted like its pill — brand for the resolved state, warning for the two
    outstanding — because a row of three identical grey glyphs would tie the option to the
    card's SHAPE while dropping the one channel that separates done from not-done at a glance.
  */
  const statusGlyph = (state: MetricInputMarkerState) => {
    const Glyph = MARKER_GLYPHS[state]
    return (
      <Glyph
        size={14}
        strokeWidth={2}
        color={state === 'confirmed' ? theme.palette.brand[500] : theme.palette.warning.main}
        aria-hidden
      />
    )
  }
  const statusOptions = [
    { label: MARKER_LABELS.confirmed, value: 'supplied', icon: statusGlyph('confirmed') },
    { label: MARKER_LABELS.unconfirmed, value: 'unconfirmed', icon: statusGlyph('unconfirmed') },
    { label: MARKER_LABELS.unset, value: 'needed', icon: statusGlyph('unset') },
  ]

  /*
    ── ⚠️ "N OF M SUPPLIED" SELECTS **TWO** STATUSES, AND THAT IS THE BUG FIX ──────────

    `supplied` matches `state !== 'unset'`, so the count — and the cards it filters to —
    include confirmed AND provisional figures. Selecting only `['supplied']` produced a
    correct CARD LIST under a dropdown reading just "Supplied", while one of the two cards
    on screen plainly wore a "Needs review" pill. The control under-described its own result.

    That is the harder half of header/filter agreement to catch: the previous pass verified
    the COUNT against the CARD COUNT and they matched, so the disagreement was in the LABEL
    only. Checking a number against a number cannot find it; the check has to be "does the
    control name everything the reader can see".

    So the supplied count selects both members of its own population. The card set is
    unchanged — `unconfirmed` is a strict subset of `supplied`, so OR-ing them is a no-op on
    the result — and the dropdown now names both.

    ⚠️ THE PRESSED TEST IS SET EQUALITY, NOT `length === 1`. The earlier version asked
    "is exactly one thing selected", which was only ever a proxy for "is the selection this
    count's set" and stops being true the moment a count legitimately selects two. Compared
    as SETS so the dropdown's own ordering cannot matter.
  */
  const SUPPLIED_FILTER_SET = ['supplied', 'unconfirmed']
  const REVIEW_FILTER_SET = ['unconfirmed']
  const isSameSet = (a: string[], b: string[]) =>
    a.length === b.length && a.every(v => b.includes(v))
  const isSuppliedFilter = isSameSet(statusFilter, SUPPLIED_FILTER_SET)
  const isReviewFilter = isSameSet(statusFilter, REVIEW_FILTER_SET)

  /**
   * ⚠️ THE HAYSTACK IS NOT JUST THE LABEL, and that is the point of the feature.
   *
   * Someone arrives thinking "I want to fix my ROI" or "why is return fraud blank",
   * not "gross margin" — they are searching for the PROBLEM, and the figure's own
   * name is the one word they do not have. So a figure matches on:
   *
   *   its label            "gross margin"
   *   its description      "convert retained units into profit"
   *   the metrics it feeds "Return Fraud Prevented", "Counterfeit Deflection"
   *   those metrics' formulas   "prorated to the period, multiplied by coverage"
   *
   * The metric names are the load-bearing addition: they are what turn "the number
   * I want to change" into the field that changes it.
   *
   * ## Combining semantics, stated because they are not guessable from the UI
   *
   *   WITHIN one dropdown   OR   — pick Finance and Legal to see what either cares about
   *   BETWEEN the two       AND  — Finance AND Brand Protection narrows
   *   with the search       AND
   *
   * OR-within is what makes a multiselect worth having: AND-within would return nothing
   * for almost every pair, since a metric is rarely tagged with two whole role sets.
   *
   * A figure's roles/solutions are the UNION over the metrics it feeds — see
   * `metricInputTaxonomy`, which is also where the one input with no solutions at all is
   * explained.
   */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()

    const matched = catalogue.filter(d => {
      if (q) {
        const haystack = [
          d.label,
          d.description,
          ...d.metric_titles,
          ...d.metric_ids.map(id => METRIC_BY_ID[id]?.formula ?? ''),
        ]
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(q)) return false
      }

      if (statusFilter.length > 0) {
        const state = metricInputMarkerState(valueForKey(values, d.key))
        if (!statusFilter.some(s => STATUS_MATCHERS[s]?.(state))) return false
      }

      if (roleFilter.length === 0 && solutionFilter.length === 0) return true

      const { roles, solutions } = metricInputTaxonomy(d)
      if (roleFilter.length > 0 && !roleFilter.some(r => roles.includes(r as MetricRole))) {
        return false
      }
      if (
        solutionFilter.length > 0 &&
        !solutionFilter.some(s => solutions.includes(s as MetricSolution))
      ) {
        return false
      }
      return true
    })

    /*
      Always A–Z. The direction used to be a control; it was removed because it could not
      say what it did — the `Sort:` label beside it existed only because a bare "A - Z"
      does not read as a sort, and a control needing a label bolted on to be legible is a
      control doing too little for the 131.6px it cost on the row.
      Z–A answers no question anyone brings to a list of figures to supply.

      `localeCompare` rather than `<`, so an accented or cased title orders the way a
      reader expects rather than by code point.
    */
    return [...matched].sort((a, b) => a.label.localeCompare(b.label))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, values, query, roleFilter, solutionFilter, statusFilter])

  // §19 — first load only, and the skeleton mirrors the real layout: an intro
  // paragraph, then one card per field at roughly the height a field occupies.
  if (isLoading && !data) {
    return (
      <>
        {/*
          The REAL header, not a skeleton of one — the page is `selfTitled`, so this
          component owns the title in the loading frame too, and a grey bar where a
          known string belongs is a stand-in for nothing. Same block, same offsets, so
          the title does not appear late and shift the page under it (§19).

          The progress line IS skeletoned: unlike the title it is not known until the
          response lands, and it sits in the same right-hand slot at roughly its own
          width so the header row does not reflow when the number arrives.
        */}
        <StickyFormHeader
          title="Business figures"
          nested
          top={stickyTop}
          /* The progress stand-in rides the TITLE row now, in `actions`, because that is
             where the loaded page puts the real one. Same fixed width and the same 18px
             text height, so the title's right-hand end does not move when the count
             arrives. */
          actions={<Skeleton sx={{ flex: `0 0 ${PROGRESS_WIDTH_PX}px`, height: 18 }} />}
          bottomGap={3}
        >
          {/* THE ONE CONTROL ROW, mirroring the loaded page part for part — §19 is
              footprint parity, and a lone search stand-in would be the right height but
              the wrong shape, which is what makes a skeleton read as a different page.

              Both stand-ins are FIELD_HEIGHT (49), the rendered height of the shared
              `Search` and the `SelectField` trigger alike. They used to be 50 and 47,
              measured off the two controls when those disagreed; VRG-703 brought both to
              49, and the skeleton reads the same token so it cannot drift from them again.

              The filter group carries the same `ml: 'auto'` as the loaded row, so the two
              controls resolve in the same PLACE rather than sliding right when the data
              lands. */}
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
            <Skeleton
              sx={{ flex: `0 1 ${SEARCH_WIDTH_PX}px`, minWidth: 0, height: FIELD_HEIGHT, borderRadius: 2 }}
            />
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ ml: 'auto' }}
            >
              <Skeleton sx={{ flex: `0 0 ${ROLE_WIDTH_PX}px`, height: FIELD_HEIGHT, borderRadius: 2 }} />
              <Skeleton sx={{ flex: `0 0 ${SOLUTION_WIDTH_PX}px`, height: FIELD_HEIGHT, borderRadius: 2 }} />
            </Stack>
          </Stack>
        </StickyFormHeader>
        <Box>
          {/* Two lines at the FULL column, matching the intro's own footprint now that
              it is no longer held to the description measure (§19 is footprint parity,
              so a 3-line 560px stand-in would drop every card below it by a line the
              moment the real text arrived). `mt: 5` matches the loaded description's own
              gap to the filter row above it. */}
          <Box sx={{ mt: 5 }}>
            <Skeleton lines={2} />
          </Box>
          {/* The SAME grid the loaded page uses, at the same spacing and breakpoints —
              a single stacked column here would have every card land in a different
              place the moment the data arrived, which is the layout shift §19 exists to
              prevent. Four stand-ins, matching the catalogue's current length. */}
          <Grid container spacing={3} sx={{ mt: 3 }}>
            {[0, 1, 2, 3].map(i => (
              <Grid item xs={12} md={6} key={i}>
              <Card fill sx={{ p: 6, height: SKELETON_CARD_HEIGHT_PX }}>
                {/*
                  Mirrors the loaded card PART FOR PART, and every number is MEASURED on the
                  rendered page rather than guessed — §19's ask is footprint parity, and a
                  skeleton of the wrong height shifts everything under it at the exact moment
                  the real content arrives, which is the defect the rule exists to prevent.

                  ⚠️ RE-MEASURED after the "Used by" panel became a label and the formula moved
                  to a tooltip. The old stand-in mirrored a card that no longer exists: it
                  carried a 106px block for the bordered panel and no stand-in at all for the
                  standing line, and totalled 371 against a card that is now 328.

                  Measured on the live cards at 1280x800, sidebar expanded (788.5 column),
                  walking `MetricInputField`'s own flex column:

                    title                        28
                      + 8 gap
                    description                  21   (one line)
                      + 16 gap  (mt: 4)
                    "Used by" label              21
                      + 8 gap   (FieldLabel's own mb, §31)
                    input                        49
                      + 12 gap  (mt: 3)
                    standing line                42   (STANDING_MIN_HEIGHT_PX, 2 lines)
                      + 21      (the `mt: auto` slack on this particular card)
                      + 12      (the action row's pt: 3)
                    button                       40
                    ------------------------------------------------
                    + 24 padding top and bottom  =  328

                  The skeleton reproduces the same column INCLUDING `mt: 'auto'` on the button
                  block, so the leftover lands in the same place it lands on a loaded card
                  rather than being distributed between the parts.
                */}
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Skeleton sx={{ width: 260, height: 28 }} />
                  <Box sx={{ mt: 2 }}>
                    {/* `height={21}` is the description's own LINE-HEIGHT (14px body3 at
                        1.5), not `Skeleton`'s 14px default — the default renders a 14px bar
                        where a 21px line goes, which is a mismatch in the same part. */}
                    <Skeleton height={21} sx={{ maxWidth: PROSE_MEASURE_PX }} />
                  </Box>
                  {/* The "Used by" LABEL — a short line now, not the 106px bordered panel it
                      replaced. 180 is roughly "Used by Counterfeit Deflection". */}
                  <Box sx={{ mt: 4 }}>
                    <Skeleton sx={{ width: 180, height: 21 }} />
                  </Box>
                  {/* 8px, `FieldLabel`'s own label->control gap (§31) — the one place this
                      number may be written is that component, so it is mirrored, not chosen. */}
                  <Box sx={{ mt: 2 }}>
                    {/* 49, the FIELD's rendered height — not the 50 used for the search box
                        above, which is taller because of its leading magnifier. */}
                    <Skeleton sx={{ width: '100%', height: 49, borderRadius: 2 }} />
                  </Box>
                  {/* The standing line, at its own two-line floor. It had NO stand-in before,
                      which is most of why the old skeleton was the wrong height. */}
                  <Box sx={{ mt: 3 }}>
                    <Skeleton lines={2} height={21} />
                  </Box>
                  <Box sx={{ mt: 'auto', pt: 3 }}>
                    <Skeleton sx={{ width: 67, height: 40, borderRadius: 2 }} />
                  </Box>
                </Box>
              </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </>
    )
  }

  /*
    The progress line lives in the header's `actions` slot — the title row — rather
    than on the control row. It sat on the control row while that row held only the
    search; once the two filters moved up beside the search there was no room left
    (300 + 208 + 512 + gaps = 1044 against 788.5), and the title row already exists,
    so putting it back there costs no band height.
  */
  const progressLine = catalogue.length > 0 ? (
            <Typography
              variant="body3"
              color="text.secondary"
              /*
                Fixed width and right-aligned, so the sentence grows leftwards into its
                own reserved space as it changes. Letting it size to content would move
                the search box's right edge on every save.
              */
              sx={{ flex: `0 0 ${PROGRESS_WIDTH_PX}px`, textAlign: 'right' }}
            >
              {/*
                ── THE COUNTS ARE THE STATUS FILTER'S OTHER FACE ──────────────────────────

                Both halves of this sentence are buttons that apply the Status filter, and
                they map onto it EXACTLY rather than approximately — which is the only reason
                they are clickable at all:

                    "N of M supplied"   ->  `['supplied', 'unconfirmed']`  (state !== 'unset')
                    "K needs review"    ->  `['unconfirmed']`              (state === 'unconfirmed')

                ⚠️ The supplied count selects BOTH, because its population contains both —
                see `SUPPLIED_FILTER_SET` for the bug that came of selecting only one.

                `answered` and `unconfirmedCount` are computed from `markerStates` by those
                very predicates, so the number in the sentence and the number of cards left
                standing after the click are the same derivation. A count that filtered to a
                DIFFERENT population than it counted would be worse than an inert count.

                ⚠️ AGREEMENT IS STRUCTURAL, NOT MAINTAINED. These write `statusFilter`, the
                same state the Status dropdown reads and writes — there is no second copy to
                keep in sync, so the dropdown cannot fail to show what the header just did.

                They TOGGLE rather than only setting: clicking the live filter off is the way
                back, and it means the header never asserts a filter the reader cannot undo
                from the same spot they turned it on.
              */}
              <Box
                component="button"
                type="button"
                onClick={() => setStatusFilter(isSuppliedFilter ? [] : SUPPLIED_FILTER_SET)}
                aria-pressed={isSuppliedFilter}
                sx={COUNT_FILTER_SX}
              >
                {answered} of {catalogue.length} supplied
              </Box>
              {unconfirmedCount > 0 && (
                // "needs review" / "need review", matching the pill on the cards below.
                // The header, the pills and the status filter are one vocabulary — a
                // reader who sees "1 needs review" here has to be able to find the card
                // wearing that exact word, and can now do it in one click.
                <>
                  {' · '}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setStatusFilter(isReviewFilter ? [] : REVIEW_FILTER_SET)}
                    aria-pressed={isReviewFilter}
                    sx={{ ...COUNT_FILTER_SX, color: 'warning.main' }}
                  >
                    {unconfirmedCount} {unconfirmedCount === 1 ? 'needs' : 'need'} review
                  </Box>
                </>
              )}
            </Typography>
  ) : null

  return (
    <>
      {/*
        ── WHAT PINS, AND WHAT DELIBERATELY DOES NOT ────────────────────────────────

        PINNED: the title row, and under it ONE row holding the search box with the
        progress line right-aligned against it.

        NOT PINNED: the filter row, the description, the cards.

        ## The measurement that decided it

        Measured on the running page at 1280x800, sidebar EXPANDED (the tightest column,
        788.5px), the shell's own "Settings" header occupies the first 92.5px of the
        scrollport. Against an 800px viewport:

            title + search/progress pinned, filters scroll   124px band -> 216.5  = 27.1%
            all three rows pinned                            191px band -> 283.5  = 35.4%

        35.4% is past the third-of-the-screen line this file already drew once — the
        earlier one-row layout was kept on one row precisely because wrapping it to two
        took the band to 191px, and that was rejected then for the same reason it is
        rejected now. Pinning all three rows would spend more than a third of the
        viewport permanently on chrome for a list that currently holds four cards.

        ## Why the FILTER row is the one that gives way

        Of the three candidates it is the weakest, and the test is whether the control is
        CONSULTED WHILE SCROLLING (§68):

          - The progress line CHANGES as you work and is the feedback for a save that
            happens at a field, mid-scroll. "3 of 4 supplied" moving in the pinned band
            is how you learn the save landed; "· 1 needs review" is an outstanding-action
            count for a card that may be off screen. It must stay visible.
          - The search is the FINDER for a list long enough to need one, so it is least
            useful at exactly the moment it would scroll away — once you are deep in the
            list that motivated using it.
          - The filters are SET ONCE and then read off the result, not operated
            continuously. Scrolling back to change one is a deliberate act, unlike
            glancing at a count.

        So the filter row sits immediately below the pinned block in normal flow, 12px
        under the search row — the same gap the title row has to the search row, so the
        two control rows read as one group even though only the first of them pins.

        THE DESCRIPTION STILL DOES NOT PIN. It is read-once prose, and putting it in the
        block would spend permanent band height on something already read.
      */}
      <StickyFormHeader
        title="Business figures"
        nested
        top={stickyTop}
        actions={progressLine}
        bottomGap={3}
      >
        {/*
          ── ONE CONTROL ROW: SEARCH LEFT, THE TWO FILTERS RIGHT ─────────────────────

          This was two rows — a search row here, and a `Filter:` [Role][Solution][Status]
          … `Sort:` [A - Z] row in flow below. Both group labels are gone, and so are two
          of the four controls.

          `Filter:` / `Sort:` were the only rendered instances of that pattern anywhere in
          `apps/platform/src`, and the house had already rejected it once: the market-review
          page removed a `"Status: "` value prefix as wasteful. A placeholder that states
          the unfiltered state ("Any role") already says it is a filter.

          WHAT WENT, AND WHY EACH WAS AFFORDABLE:

            Sort   [A - Z]        131.6px  — see the sort in `filtered`. A direction toggle
                                             whose own label had to explain that it was a
                                             sort.
            Status [Any status]   192.0px  — the header's progress line is the same filter:
                                             both halves of "N of M supplied · K needs
                                             review" write `statusFilter`, the state that
                                             dropdown read and wrote. The two states anyone
                                             filters by stayed one click away; what was lost
                                             is arbitrary MULTI-state combinations, which is
                                             a rare ask paying a permanent 180px.

          THE ARITHMETIC, against the MEASURED 762px row (1280 viewport, sidebar expanded —
          the container runs x=493 to x=1255):

            search 238 + 12 + role 220 + 12 + solution 280  =  762   exactly, no wrap

          The search asks for 300 (`flex: 0 1 300px`), absorbs the 62px deficit and settles
          at 238. The previous shape needed 846.4 and WRAPPED here; keeping Status would
          need 1016 and leave the search 12px.

          ⚠️ The whole row is gated on a non-empty catalogue. It used to render above the
          "Nothing to supply right now" card — a search, three filters and a sort over an
          empty list, every one of them inert.

          ⚠️ `ml: 'auto'` ON THE FILTER GROUP, NOT `justifyContent: 'space-between'` here.
          They differ exactly in the wrapped case: `justify-content` applies PER FLEX LINE,
          so a wrapped second line justifies its one item to flex-START and the filters land
          on the LEFT. An auto margin belongs to the ITEM, so it stays right-aligned on
          whichever line it lands on.

          The search is the shared `Search` (§28), not a bare MUI field, so it carries
          the house magnifier, the clear "×" and the field chrome every other search on
          the platform has.
        */}
        {catalogue.length > 0 ? (
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
          useFlexGap
        >
          <Box sx={{ flex: `0 1 ${SEARCH_WIDTH_PX}px`, minWidth: 0 }}>
            <Search
              value={query}
              onChange={setQuery}
              onClear={() => setQuery('')}
              // See `filtered` — no request is issued, so a debounce would be latency
              // bought for nothing.
              debounce={false}
              fullWidth
              placeholder={SEARCH_PLACEHOLDER}
              /*
                ⚠️ `minWidth: 0` UNDOES A HARDCODED 300 IN THE SHARED `Search`, and without
                it this control silently overflows its own slot.

                `Search` sets `minWidth: 300` on its field alongside `fullWidth`, which are
                contradictory instructions — "fill your container" and "never be smaller
                than 300" — and the 300 wins. MEASURED when the search shared a row with
                four other controls: the wrapping Box laid out at 142.5px while the field
                inside it painted 300px, so the input ran 157px underneath the Sort
                dropdown. Nothing looked wrong on an EMPTY field, which is why it survived:
                the overflow is invisible until someone types, and then the text they typed
                is behind another control.

                The search is now CAPPED at exactly 300, so at 1280 the override and the
                component's own floor agree and this line changes nothing. It is kept for the
                one case where they still disagree: a settings column narrower than 300, where
                `flex: 0 1` asks the box to shrink and the hardcoded 300 would otherwise
                refuse — which is precisely the overflow-behind-a-neighbour defect above.
              */
              sx={{ minWidth: 0 }}
            />
          </Box>

          {/*
            ⚠️ `flexWrap: 'nowrap'` HERE IS LOAD-BEARING, and `wrap` is the bug it fixes.

            With `wrap` on this inner group, its MIN-CONTENT width collapses to the widest
            single child (280) instead of the full 512. The outer row then "fits" by wrapping
            the two filters onto separate lines rather than by shrinking the search — which
            is exactly backwards, since the search is the item with `flex: 0 1` and is the
            one designed to give. MEASURED in that state at 1280: role at y=149, solution at
            y=208, and the search squeezed to 225px anyway. Two stacked filters AND a
            squeezed search, the worst of both.

            `nowrap` pins this group's min-content at 512, so the outer row has exactly one
            way to fit and takes it: shrink the search. At the measured 762px container that
            settles at 762 − 12 − 512 = 238px of search, on one line.
          */}
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            flexWrap="nowrap"
            useFlexGap
            sx={{ ml: 'auto', flexShrink: 0 }}
          >
            <Box sx={{ flex: `0 0 ${ROLE_WIDTH_PX}px`, minWidth: 0 }}>
              <SelectField
                multiple
                options={roleOptions}
                value={roleFilter}
                onChange={v => setRoleFilter(v as string[])}
                /*
                  "Any role", not "Role" — the placeholder states the UNFILTERED state, so an
                  untouched dropdown says what it is currently DOING rather than naming an axis.

                  This is now the ONLY thing saying these are filters, the `Filter:` label
                  having gone, and it is enough: "Any role" cannot be read as anything but a
                  filter at rest, whereas "Role" would name an axis and state nothing.
                */
                placeholder="Any role"
                footer={roleFooter}
              />
            </Box>

            <Box sx={{ flex: `0 0 ${SOLUTION_WIDTH_PX}px`, minWidth: 0 }}>
              <SelectField
                multiple
                options={solutionOptions}
                value={solutionFilter}
                onChange={v => setSolutionFilter(v as string[])}
                placeholder="Any solution"
              />
            </Box>
          </Stack>
        </Stack>
        ) : null}
      </StickyFormHeader>

      <Box>

      {/*
        §40 — the page's descriptive text sits directly under the title, not at the
        foot of the page under the content it qualifies. A caveat read after the
        decisions are made cannot inform them.
      */}
      {/*
        ── FULL COLUMN WIDTH, and the two previous narrowings were the error ──────────

        This carried `maxWidth: PROSE_MEASURE_PX` on a characters-per-line argument, and
        was narrowed twice (1100 -> 720 -> 560) while the reader kept asking for it to be
        WIDER. Two disagreements between a measurement and the person looking at the
        screen means the measurement is answering the wrong question, and here it was:
        the 45–90 CPL range governs SUSTAINED reading, and this is a standfirst read once
        on arrival. At 560 it was four lines; at the column it is two. Two lines is one
        return sweep, so the cost the range exists to prevent is not being paid — see
        `PROSE_MEASURE_PX`, which is now the DESCRIPTION measure only.

        ⚠️ It is NOT moved into `StickyFormHeader`, which is what "put it in the header"
        would mean structurally. That component is ONE `position: sticky` box holding the
        title row and the search, so anything placed between them pins with them — and
        this is read-once prose in a band that already costs 124px on top of the shell's
        92.5 (216.5 of an 800px viewport).

        ⚠️ IT NOW SITS BELOW THE FILTER ROW RATHER THAN DIRECTLY BELOW THE BAND, which is
        the one thing this restructure cost. Its position RELATIVE to the header block is
        unchanged — it has always followed the page's controls rather than sitting between
        the title and them — but there is one more control row above it than there was.
        The alternative was to seat it between the search row and the filter row, which
        would have split the two control rows the reader asked to have adjacent. If the
        §40 reading wins on review, swap this Typography above the filter `Stack`; nothing
        else has to move.

        `mt: 5` matches the gap the band used to give it, so the prose sits the same
        distance below the controls as before.
      */}
      <Typography
        variant="body3"
        color="text.secondary"
        sx={{ display: 'block', lineHeight: 1.5, mt: 5 }}
      >
        Some analytics metrics need a figure only you can supply. Vera measures what it
        can see (scans, transfers, recoveries) and combines it with these to show what
        that activity is worth. Nothing here is shared outside your company, and every
        figure is only ever applied to your own data.
      </Typography>

      {catalogue.length === 0 ? (
        <Card sx={{ p: 6, mt: 6 }}>
          <Typography variant="body2" sx={{ display: 'block', fontWeight: 600 }}>
            Nothing to supply right now
          </Typography>
          <Typography variant="body3" color="text.secondary" sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}>
            No metric currently asks for a figure from you. This list is derived from
            Vera&apos;s metric catalogue, so it grows on its own when a metric starts
            needing one.
          </Typography>
        </Card>
      ) : filtered.length === 0 ? (
        /*
          A filtered list that renders nothing is indistinguishable from a broken page,
          so the no-match case is stated and names EVERY criterion that produced it —
          not only the search term. With three ways to empty the list, naming just the
          one the reader last touched leaves them hunting for which dropdown did it.

          The distinction from "nothing to supply right now" above is the point: that
          one means the catalogue is empty, this one means your criteria are. They read
          as completely different sentences and only this one offers a way out.
        */
        <Card sx={{ p: 6, mt: 6 }}>
          <Typography variant="body2" sx={{ display: 'block', fontWeight: 600 }}>
            No figure matches these filters
          </Typography>
          <Typography
            variant="body3"
            color="text.secondary"
            sx={{ display: 'block', mt: 2, lineHeight: 1.5, maxWidth: PROSE_MEASURE_PX }}
          >
            {[
              query.trim() ? `the search “${query.trim()}”` : null,
              roleFilter.length
                ? `${roleFilter.length === 1 ? 'role' : 'roles'} ${roleFilter
                    .map(r => METRIC_ROLE_LABELS[r as MetricRole])
                    .join(', ')}`
                : null,
              solutionFilter.length
                ? `${solutionFilter.length === 1 ? 'solution' : 'solutions'} ${solutionFilter
                    .map(s => METRIC_SOLUTION_LABELS[s as MetricSolution])
                    .join(', ')}`
                : null,
              statusFilter.length
                ? `status ${statusFilter
                    .map(s => statusOptions.find(o => o.value === s)?.label ?? s)
                    .join(', ')}`
                : null,
            ]
              .filter(Boolean)
              .reduce<string>(
                (acc, part, i, arr) =>
                  acc + (i === 0 ? 'Showing figures matching ' : i === arr.length - 1 ? ' and ' : ', ') + part,
                '',
              )}
            . Search covers each figure&apos;s name and description and the names of the
            metrics it feeds; role and solution come from those metrics too.{' '}
            {catalogue.length} {catalogue.length === 1 ? 'figure is' : 'figures are'}{' '}
            available in total.
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Button
              variant="secondary"
              onClick={() => {
                setQuery('')
                setRoleFilter([])
                setSolutionFilter([])
                setStatusFilter([])
              }}
            >
              Clear filters
            </Button>
          </Box>
        </Card>
      ) : (
        /*
          ── TWO UP, NOT ONE, AND NOT THREE ──────────────────────────────────────────

          The complaint was the FIELD: a full-column card put a 1052px input under a
          one-number question, which reads as a paragraph box and gives the eye no clue
          how much is expected. Halving the card halves the field.

          Two rather than three, measured on the 1100px column at `spacing={3}`:

              3-up   358.7px card ->  310.7px of content
              2-up   544.0px card ->  496.0px of content

          A card is not just the field. It carries a 20px title, a description, the
          bordered "Used by" panel with a registry formula sentence (~150 characters),
          the field itself and two buttons. At 310px of content that formula wraps to
          five lines and the card grows TALLER than the row it saved — three narrow
          columns of tall cards is not more compact than two, it is just harder to read.
          496px keeps the description near its own reading measure and the formula to
          two lines.

          Breakpoints follow `IntegrationsPage`'s idiom (`Grid` + per-breakpoint spans)
          rather than a hand-rolled grid. They key off the VIEWPORT, so `md` (900px) is
          where the settings column still has room for two: below that a form card in
          half a narrow column would be unusable, so it goes full width.
        */
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {filtered.map(definition => (
            <Grid item xs={12} md={6} key={definition.key}>
              {/*
                `height: 100%` so two cards in a row end level — their content lengths differ
                and ragged bottoms across a grid read as broken rather than as varied.

                ⚠️ `fill` IS LOAD-BEARING, not decoration. `Card` wraps its children in TWO
                `Stack`s, and without `fill` those wrappers are auto-height — so a child asking
                for `height: 100%` resolves against a 257px wrapper inside a 328px card and
                stretches to nothing. `fill` is the shared component's own switch for exactly
                this (`flex: 1; minHeight: 0` on the Paper and both wrappers), which is why it
                is used here rather than reaching past `Card` with descendant selectors in this
                file (§28/§47 — the base already solves it).

                What depends on it: `MetricInputField` is a flex column that pins its action
                row to the bottom with `mt: 'auto'`. That needs the column to actually BE the
                card's height, so `fill` is what makes the pin work at all — measured, without
                it the buttons kept floating 21px above the card floor.
              */}
              <Card fill sx={{ p: 6, height: '100%' }}>
                <MetricInputField
                  definition={definition}
                  value={valueForKey(values, definition.key)}
                  roi={roi}
                  isSaving={saving}
                  onSave={(stored, currency) => handleSave(definition.key, stored, currency)}
                  // The company's CURRENT reporting currency — the default for a NEW
                  // figure. An existing one keeps its own and is flagged stale.
                  reportingCurrency={
                    companyQuery.data?.reporting_currency ?? DEFAULT_REPORTING_CURRENCY
                  }
                  onRemove={() => handleRemove(definition.key)}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ mt: 6 }}>
        {/* A real anchor — navigation must survive cmd-click and "open in new tab",
            which a button swallows. `@repo/ui`'s Button is also not polymorphic. */}
        <Link
          component={RouterLink}
          to={ROUTES.ANALYTICS}
          state={referrer}
          variant="body3"
          underline="hover"
          color="text.secondary"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
        >
          See these figures at work in Analytics
          <ArrowUpRightIcon size={16} strokeWidth={1} />
        </Link>
      </Box>
      </Box>
    </>
  )
}
